/**
 * What the HUD tracker and the Journal badge say about the player's quests.
 *
 * Pure: it reads the store's shape and returns plain rows, so the DOM layer has
 * nothing to decide and the numbers are testable without one. Both surfaces
 * come from {@link trackedQuests}, which is what keeps the badge ("3") and the
 * tracker (three rows) from ever disagreeing.
 *
 * A quest counts while it is HELD: still being worked on, or finished with the
 * reward waiting. Claimed, expired and merely-offerable ones do not.
 */
import { itemLabel } from './quest-state';
import type {
  PeriodicQuestView, QuestState, StoryObjectiveView, StoryQuestView,
} from './quest-state.types';

export interface TrackedQuest {
  /** Stable key: the slug, prefixed by where it came from. */
  key: string;
  source: 'story' | 'job';
  name: string;
  /** The one thing to do next, in player words. */
  task: string;
  /** `held/needed` style counter, or null when the task has no count. */
  count: string | null;
  /** 0..1 — for the thin bar under the row. */
  fraction: number;
  /** Every objective is done; the reward is waiting. */
  ready: boolean;
}

/** How many rows the tracker shows. */
export const TRACKER_ROWS = 3;

function storyTask(q: StoryQuestView): Pick<TrackedQuest, 'task' | 'count' | 'fraction'> {
  const next: StoryObjectiveView | undefined = q.objectives.find((o) => !o.complete);
  if (!next) return { task: 'Ready to collect', count: null, fraction: 1 };
  if (next.kind === 'DELIVER') {
    // Held in the hideout is what a hand-over can use; progress only moves once
    // it is actually handed in, so the stash count is the honest number.
    const owed = q.outstanding.find((o) => o.itemKey === next.targetKey);
    const have = owed ? Math.min(owed.held, owed.needed) : next.progress;
    const need = owed ? owed.needed : next.targetCount;
    return {
      task: next.description || `Bring ${itemLabel(next.targetKey)}`,
      count: `${have}/${need}`,
      fraction: need > 0 ? have / need : 0,
    };
  }
  return { task: next.description, count: null, fraction: q.total > 0 ? q.completed / q.total : 0 };
}

function jobRow(j: PeriodicQuestView): TrackedQuest {
  const target = Math.max(1, j.targetCount);
  return {
    key: `job:${j.userQuestId}`,
    source: 'job',
    name: j.name,
    task: j.claimable ? 'Ready to collect' : j.description,
    count: j.claimable ? null : `${Math.min(j.progress, target)}/${target}`,
    fraction: Math.min(1, j.progress / target),
    ready: j.claimable,
  };
}

/**
 * Every quest the player holds: story steps first (the thread they are
 * following), then the day's and week's jobs. Ready-to-collect rows float to
 * the top of their group so the reward waiting is never the one cut off.
 */
export function trackedQuests(state: Pick<QuestState, 'board' | 'jobs'>): TrackedQuest[] {
  const story: TrackedQuest[] = (state.board?.active ?? [])
    .filter((q) => q.status === 'ACTIVE' || q.status === 'COMPLETED')
    .map((q) => ({
      key: `story:${q.slug}`, source: 'story' as const, name: q.name, ready: q.claimable, ...storyTask(q),
    }));
  const jobs = [...(state.jobs?.daily ?? []), ...(state.jobs?.weekly ?? [])]
    .filter((j) => j.status === 'ACTIVE' || j.status === 'COMPLETED')
    .map(jobRow);
  const readyFirst = (a: TrackedQuest, b: TrackedQuest): number => Number(b.ready) - Number(a.ready);
  return [...story.sort(readyFirst), ...jobs.sort(readyFirst)];
}

/** The Journal button's badge. */
export function activeQuestCount(state: Pick<QuestState, 'board' | 'jobs'>): number {
  return trackedQuests(state).length;
}
